'use client';
import React, { useState } from 'react';

import Bookmaker from '@src/components/Icons/Bookmaker';

import CardMetric from '@src/components/Shared/CardMetric';
import NextLink from '@src/components/Shared/NextLink';
import FractionDisplay from '@src/components/Shared/FractionDisplay';

import useIsMobile from '@src/hooks/useIsMobile';
import useDebounce from '@src/hooks/useDebounce';

import ForceInput from '../ForceInput/ForceInput';
import UnitDropdown from '../UnitDropdown/UnitDropdown';
import { UnitTypes } from '../UnitDropdown/UnitDropdown.types';
import { RelativeStrengthCardPropTypes } from './RelativeStrengthCard.types';

const unitOptions: UnitTypes[] = [
  { id: 'metric', name: 'Metric', unit: 'N/kg' },
  { id: 'imperial', name: 'Imperial', unit: 'N/lb' },
];

const RelativeStrengthCard: React.FC<RelativeStrengthCardPropTypes> = () => {
  const isMobile = useIsMobile();

  // ―― “committed” values (after hitting Calculate on mobile) ――
  const [peakForce, setPeakForce] = useState('');
  const [bodyWeight, setBodyWeight] = useState('');

  // ―― temporary input slots (for mobile) ――
  const [tempPeak, setTempPeak] = useState('');
  const [tempWeight, setTempWeight] = useState('');
  const [needsCalc, setNeedsCalc] = useState(false);

  // ―― which system are we in? ――
  const [unit, setUnit] = useState<'metric' | 'imperial'>('metric');

  const handleCalculate = () => {
    setPeakForce(tempPeak);
    setBodyWeight(tempWeight);
    setNeedsCalc(false);
  };

  // ―― pick the “current” text‐field values (depending on mobile + calculate) ――
  const currentPeak = isMobile && needsCalc ? tempPeak : peakForce;
  const currentWeight = isMobile && needsCalc ? tempWeight : bodyWeight;

  // ―― parse them to numbers ――
  const peakVal = parseFloat(currentPeak) || 0;
  const weightVal = parseFloat(currentWeight) || 0;

  // ―― compute Relative Strength (same formula, no conversions) ――
  const computeRS = () => {
    if (weightVal === 0) return 0;
    // Always assume peakVal is in “Newton”
    // If metric: weightVal is in kg → result is N/kg
    // If imperial: weightVal is in lb → result is N/lb
    return peakVal / weightVal;
  };
  const rs = computeRS();
  const displayRS = rs.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  // ―― debounce the displayed value by 3000 ms ――
  const debouncedRS = useDebounce(displayRS, 3000);

  // ―― fire tracking event whenever debouncedRS changes and is valid ――
  React.useEffect(() => {
    if (
      peakForce !== '' &&
      bodyWeight !== '' &&
      !isNaN(Number(debouncedRS)) &&
      isFinite(Number(debouncedRS))
    ) {
      if (typeof window !== 'undefined' && Array.isArray(window.dataLayer)) {
        window.dataLayer.push({
          event: 'msk_calculator_calculation_performed',
          eventCategory: 'msk_calculator_usage',
          eventLabel: 'msk_calculator_relative_strength',
          value: 1,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedRS]);

  // ―― unit labels and placeholders ――
  // Force is always “Newton” in both Metric and Imperial
  const inputForceUnit = 'Newton';
  const forcePlaceholder = 'Newton';

  // Mass is either “Kilograms” (metric) or “Pounds” (imperial)
  const inputMassUnit = unit === 'metric' ? 'Kilograms' : 'Pounds';
  const massPlaceholder = unit === 'metric' ? 'Kilograms' : 'Pounds';

  // Final result label is “N/kg” or “N/lb”
  const resultUnit = unit === 'metric' ? 'N/kg' : 'N/lb';

  return (
    <CardMetric
      id='calculator-relative-strength'
      title='Relative Strength'
      productIcons={[
        {
          alt: 'NordBord App Icon',
          url: 'https://images.ctfassets.net/98s79sqwuajy/6iS357n4siVSm6nFZeci6q/544cd567518357e813ac48f15dae44f4/nordbord-app-icon.svg',
        },
        {
          alt: 'ForceFrame App Icon',
          url: 'https://images.ctfassets.net/98s79sqwuajy/7dxABhvlBmXLF0UuYpAdss/883fc648f9bc2f93d9d05d9095800e67/forceframe-app-icon.svg',
        },
        {
          alt: 'DynaMo App Icon',
          url: 'https://images.ctfassets.net/98s79sqwuajy/5KQtYsCw5caSZvIuohrRmm/c174bb8572e3170009776e40c21f18e9/dynamo-app-icon.svg',
        },
        {
          alt: 'ForceDecks App Icon',
          url: 'https://images.ctfassets.net/98s79sqwuajy/1sZL8SrOEPwzDxDhnJCoy2/d3561b92560bdd3fe2ecd709b6f4604f/forcedecks-app-icon.svg',
        },
      ]}
    >
      <p className='mb-[2rem] text-[1.2rem]'>
        Assess peak force normalized to your subjects’ bodyweight to better compare strength values
        across individuals and groups
      </p>

      {/* Unit selector + fraction display */}
      <div className='mb-[2.4rem] flex flex-col gap-[0.5rem] sm:flex-row sm:items-center'>
        <UnitDropdown
          className='mb-[0]!'
          units={unitOptions}
          label=''
          activeUnitId={unit}
          onChange={(val) => setUnit(val as 'metric' | 'imperial')}
        />
        <FractionDisplay
          label='Relative Strength'
          numerator='<strong className="font-medium">Peak Force</strong>'
          denominator='<strong className="font-medium">Body Weight</strong>'
        />
      </div>

      {/* Peak Force Input */}
      <ForceInput
        className='mb-[1.4rem]'
        labelLines={['Peak Force', 'Maximum Assessed Force']}
        activeUnit={inputForceUnit}
        placeholder={forcePlaceholder}
        value={isMobile ? tempPeak : peakForce}
        onChange={(e) => {
          if (isMobile) {
            setTempPeak(e.target.value);
            setNeedsCalc(true);
          } else {
            setPeakForce(e.target.value);
          }
        }}
      />

      {/* Subject Mass Input */}
      <ForceInput
        className='mb-[1.4rem]'
        labelLines={['Subject Mass', 'Body Weight']}
        activeUnit={inputMassUnit}
        placeholder={massPlaceholder}
        value={isMobile ? tempWeight : bodyWeight}
        onChange={(e) => {
          if (isMobile) {
            setTempWeight(e.target.value);
            setNeedsCalc(true);
          } else {
            setBodyWeight(e.target.value);
          }
        }}
      />

      {/* Result Row */}
      <div className='mb-[3.1rem] flex items-center justify-between font-normal text-primary'>
        <span className='ml-auto text-[1.4rem] font-medium'>Relative Peak Force</span>
        <span className='ml-[2rem] border-l-[0.2rem] border-[#D9D9D9] pl-[2rem] text-[1.8rem] sm:text-[3rem]'>
          {isMobile && needsCalc ? '0.0' : isNaN(rs) || !isFinite(rs) ? '0.0' : displayRS}
          <span className='text-[1.6rem] font-light'>{`${resultUnit}`}</span>
        </span>
      </div>

      {/* Footer Link */}
      <div className='relative flex items-center gap-[1rem]'>
        <div className='absolute inset-x-[-9999px] top-[-2rem] h-[20rem] bg-[#F8F8F8]' />
        <div className='flex w-full items-center gap-[1.6rem] text-[1.2rem]'>
          <Bookmaker className='relative z-10 hidden h-[2rem] w-[1.6rem] max-w-[1.6rem] shrink-0 text-primary md:block' />
          <p className='text-pretty'>
            Find more information about how relative peak forces are applied, in Alex Natera's{' '}
            <NextLink
              className='font-light underline'
              href='https://valdperformance.com/news/pre-season-assessments-and-monitoring'
            >
              Run-Specific Isometrics
            </NextLink>{' '}
            blog.
          </p>
        </div>

        {/* “Calculate” button on mobile only */}
        {isMobile && (
          <button
            onClick={handleCalculate}
            className='common-btn-transition right-0 bottom-0 relative ml-auto inline-flex h-[3.7rem] max-h-[3.7rem] items-center justify-center rounded-[0.6rem] border-primary bg-primary px-[2rem] py-[0.8rem] text-[1.6rem] font-normal leading-[1.6rem] tracking-[-0.0304rem] text-[#fff]! hover:border-primary--v2 hover:bg-primary--v2 hover:text-primary--v2'
          >
            Calculate
          </button>
        )}
      </div>
    </CardMetric>
  );
};

export default RelativeStrengthCard;
